/**
 * Mappers for data conversion between API and event form
 */

import {
  extractTimeFromISO,
  formatDatePickerToISO,
  formatISOToDatePicker,
  formatToDateOnly,
  formatToISO
} from '@/utils/dateConverter/dateConverter'
import { getS3BaseUrl } from '@/utils/envUtils'
import {
  convertFaqFromApiFormat,
  convertFaqToApiFormat
} from '@/utils/eventRegulationConverter/eventRegulationConverter'

const mapEventDays = (eventDays, apiData) => {
  if (eventDays.length > 0) {
    return eventDays.map((day) => ({
      date: day.eventDate || '',
      startTime: extractTimeFromISO(day.startTime) || '',
      endTime: extractTimeFromISO(day.endTime) || '',
      ticketQuantity: (day.quantityAvailable || day.quantity)?.toString() || '',
      quantityAvailable: (day.quantityAvailable || day.quantity)?.toString() || '',
      originalQuantity: day.quantity || 0,
      limitPerCpf: day.redeemLimit === null || day.redeemLimit === undefined ? '' : day.redeemLimit.toString(),
      originalRedeemLimit: day.redeemLimit
    }))
  }

  return [
    {
      date: apiData.eventDate || '',
      startTime: extractTimeFromISO(apiData.startTime) || '',
      endTime: extractTimeFromISO(apiData.endTime) || '',
      ticketQuantity: (apiData.quantityAvailable || apiData.quantity)?.toString() || '',
      quantityAvailable: (apiData.quantityAvailable || apiData.quantity)?.toString() || '',
      originalQuantity: apiData.quantity || 0,
      limitPerCpf:
        apiData.redeemLimit === null || apiData.redeemLimit === undefined ? '' : apiData.redeemLimit.toString(),
      originalRedeemLimit: apiData.redeemLimit
    }
  ]
}

const joinTexts = (arr, key = 'text') =>
  (arr || [])
    .map((item) => item[key])
    .filter((text) => text?.trim())
    .join('<div><br></div>')

const processFaqData = (faq) => {
  if (!faq) {
    return [{ answer: '', question: '' }]
  }

  const faqData = typeof faq === 'string' ? JSON.parse(faq) : faq
  if (Array.isArray(faqData) && faqData.length > 0) {
    return convertFaqFromApiFormat(faqData)
  }

  return [{ answer: '', question: '' }]
}

const extractRulesText = (rulesData) => {
  if (rulesData?.questions && Array.isArray(rulesData.questions)) {
    return joinTexts(rulesData.questions)
  }

  if (rulesData?.sections && Array.isArray(rulesData.sections)) {
    return joinTexts(rulesData.sections)
  }

  if (typeof rulesData === 'string') {
    return rulesData
  }

  return ''
}

const processRulesData = (rules) => {
  if (!rules) {
    return ''
  }

  if (typeof rules === 'string') {
    if (rules.trim().startsWith('{') || rules.trim().startsWith('[')) {
      try {
        const rulesData = JSON.parse(rules)
        return extractRulesText(rulesData)
      } catch {
        return rules
      }
    }
    return rules
  }

  return extractRulesText(rules)
}

const mapRedeemPeriod = (isoDate) => {
  if (!isoDate) {
    return { data: '', hora: '' }
  }

  return {
    data: formatISOToDatePicker(isoDate) || '',
    hora: extractTimeFromISO(isoDate) || ''
  }
}

const buildImageUrl = (image, businessUnit = 'GB', tag = 'exclusive-events') => {
  if (!image) {
    return null
  }

  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image
  }

  const S3_BASE_URL = getS3BaseUrl()

  const cleanImageName = image.split(':')[0]

  if (cleanImageName.includes('/')) {
    return `${S3_BASE_URL}/${cleanImageName}`
  }

  const tagPath = tag === 'FACTORY_VISIT' ? 'factory-visit' : 'exclusive-events'
  return `${S3_BASE_URL}/${businessUnit}/${tagPath}/${cleanImageName}`
}

const deriveEventLimitPerCpf = (days) => {
  if (!Array.isArray(days) || days.length === 0) return ''
  const limits = days.map((d) => d.limitPerCpf).filter((v) => v !== '' && v !== undefined)
  if (limits.length === 0) return ''
  const first = limits[0]
  return limits.every((v) => v === first) ? first : ''
}

export const mapApiDataToFormValues = (apiData) => {
  const eventDays = apiData.eventDays || []
  const mappedDias = mapEventDays(eventDays, apiData)
  const mappedFaq = processFaqData(apiData.faq)
  const mappedRegulamento = processRulesData(apiData.rules)
  const cost = apiData.benefit?.cost || ''

  const imageUrl = buildImageUrl(
    apiData.image,
    apiData.benefit?.businessUnit || 'GB',
    apiData.tag || 'EXCLUSIVE_EVENTS'
  )

  const mappedBrands = {
    BOT: apiData.availableBrand?.BOT ?? false,
    QDB: apiData.availableBrand?.QDB ?? false,
    BLZ: apiData.availableBrand?.BLZ ?? false,
    EUD: apiData.availableBrand?.EUD ?? false
  }

  const mappedRedeemStart = mapRedeemPeriod(apiData.redeem?.start)
  const mappedRedeemEnd = mapRedeemPeriod(apiData.redeem?.end)

  const eventLimitPerCpf =
    apiData.redeemLimit === null || apiData.redeemLimit === undefined
      ? deriveEventLimitPerCpf(mappedDias)
      : String(apiData.redeemLimit)

  return {
    type: apiData.tag || '',
    name: apiData.name || '',
    description: apiData.description,
    image: apiData.image || '',
    imageUrl: apiData.imageUrl || imageUrl || '',
    pointsPerTicket: cost.toString(),
    eventLimitPerCpf,
    address: apiData.address === 'online' ? '' : apiData.address || '',
    city: apiData.city || '',
    state: apiData.region || '',
    isRemoteEvent: apiData.address === 'online' || (!apiData.address && !apiData.city && !apiData.region),
    days: mappedDias,
    regulations: mappedRegulamento,
    faq: mappedFaq,
    brands: mappedBrands,
    redeemStart: mappedRedeemStart,
    redeemEnd: mappedRedeemEnd,
    benefitId: apiData.benefit?.id || null,
    quantity: apiData.quantity || 0,
    quantityAvailable: apiData.quantityAvailable || 0
  }
}

const mapAddressData = (formValues) => {
  if (formValues.isRemoteEvent) {
    return { address: 'online', city: 'Brasil', region: 'BR' }
  }
  return {
    address: formValues.address || '',
    city: formValues.city || '',
    region: formValues.state || ''
  }
}

const mapRedeemDates = (formValues, firstDay) => {
  const start =
    formValues.redeemStart?.data && formValues.redeemStart?.hora
      ? formatDatePickerToISO(formValues.redeemStart.data, formValues.redeemStart.hora)
      : new Date().toISOString()

  const end =
    formValues.redeemEnd?.data && formValues.redeemEnd?.hora
      ? formatDatePickerToISO(formValues.redeemEnd.data, formValues.redeemEnd.hora)
      : formatToISO(firstDay.date, '23:59')

  return { start, end }
}

/**
 * Converts form data to API format
 * @param {Object} formValues - Form data (including rules and unifiedDates)
 * @param {string} imageKey - Image S3 key (if uploaded or changed)
 * @param {string} benefitId - Benefit ID
 * @param {string} businessUnit - Business unit
 * @param {string} eventId - Event ID (for editing)
 * @returns {Object} Data formatted for the API
 */
export const mapFormValuesToApiData = (formValues, imageKey, benefitId, businessUnit, eventId) => {
  const firstDay = formValues.days?.[0] || {}

  const finalImageValue = imageKey || formValues.image || null

  const totalQuantityFromDays = (formValues.days || []).reduce((total, day) => {
    const editedTicketQuantity = Number.parseInt(day.ticketQuantity) || 0
    const originalDayQuantityAvailable = Number.parseInt(day.quantityAvailable) || 0

    const quantityWasChanged = editedTicketQuantity !== originalDayQuantityAvailable
    const dayQuantity = quantityWasChanged
      ? editedTicketQuantity
      : Number.parseInt(day.originalQuantity) || editedTicketQuantity

    return total + dayQuantity
  }, 0)

  const totalQuantityAvailableFromDays = (formValues.days || []).reduce((total, day) => {
    const editedTicketQuantity = Number.parseInt(day.ticketQuantity) || 0
    const originalDayQuantityAvailable = Number.parseInt(day.quantityAvailable) || 0
    const originalDayQuantity = Number.parseInt(day.originalQuantity) || originalDayQuantityAvailable

    const quantityWasChanged = editedTicketQuantity !== originalDayQuantityAvailable
    const dayQuantityAvailable = quantityWasChanged
      ? originalDayQuantityAvailable + (editedTicketQuantity - originalDayQuantity)
      : originalDayQuantityAvailable

    return total + dayQuantityAvailable
  }, 0)

  const quantity = totalQuantityFromDays
  const quantityAvailable = totalQuantityAvailableFromDays

  const cost = Number.parseInt(formValues.pointsPerTicket) || 0

  const faqApiFormat = convertFaqToApiFormat(formValues.faq)
  const faqString = faqApiFormat ? JSON.stringify(faqApiFormat) : faqApiFormat

  const rulesString = JSON.stringify(formValues.rules)
  const addressData = mapAddressData(formValues)
  const redeemDates = mapRedeemDates(formValues, firstDay)

  const parsedEventLimit =
    formValues.eventLimitPerCpf && formValues.eventLimitPerCpf !== ''
      ? Number.parseInt(formValues.eventLimitPerCpf, 10)
      : null
  const redeemLimitValue = parsedEventLimit === null || Number.isNaN(parsedEventLimit) ? null : parsedEventLimit

  const mappedData = {
    name: formValues.name,
    description: formValues.description,
    tag: formValues.type,
    address: addressData.address,
    city: addressData.city,
    region: addressData.region,
    addressLink: '',
    image: finalImageValue,
    rules: rulesString,
    faq: faqString,
    eventDate: formatToDateOnly(firstDay.date),
    startTime: formatToISO(firstDay.date, firstDay.startTime),
    endTime: formatToISO(firstDay.date, firstDay.endTime),
    quantity,
    quantityAvailable: quantityAvailable,
    ...(redeemLimitValue !== null && { redeemLimit: redeemLimitValue }),
    benefit: {
      cost: cost,
      id: 'id'
    },
    multipleDates: (formValues.days || []).length > 1,
    unifiedDates: formValues.unifiedDates,
    businessUnit: businessUnit,
    status: 'ACTIVE',
    eventDays: (formValues.days || []).map((day, index) => {
      const editedTicketQuantity = Number.parseInt(day.ticketQuantity) || 0
      const originalDayQuantityAvailable = Number.parseInt(day.quantityAvailable) || 0
      const originalDayQuantity = Number.parseInt(day.originalQuantity) || originalDayQuantityAvailable

      const quantityWasChanged = editedTicketQuantity !== originalDayQuantityAvailable

      const finalQuantity = quantityWasChanged ? editedTicketQuantity : originalDayQuantity
      const finalQuantityAvailable = quantityWasChanged
        ? originalDayQuantityAvailable + (editedTicketQuantity - originalDayQuantity)
        : originalDayQuantityAvailable

      const parsedLimit = Number.parseInt(day.limitPerCpf, 10)
      const redeemLimit = Number.isNaN(parsedLimit) ? null : parsedLimit

      return {
        name: `${formValues.name} - Dia ${index + 1}`,
        eventDate: formatToDateOnly(day.date),
        startTime: formatToISO(day.date, day.startTime),
        endTime: formatToISO(day.date, day.endTime),
        quantity: finalQuantity,
        quantityAvailable: finalQuantityAvailable,
        redeemLimit: redeemLimit,
        businessUnit: businessUnit,
        status: 'ENABLED'
      }
    }),

    availableBrand: {
      BOT: formValues.brands?.BOT ?? true,
      QDB: formValues.brands?.QDB ?? true,
      BLZ: formValues.brands?.BLZ ?? true,
      EUD: formValues.brands?.EUD ?? true
    },
    redeem: redeemDates
  }

  if (eventId) {
    mappedData.eventId = eventId
  }
  return mappedData
}
